import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, LogOut } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { humanize, money } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import type { Admission } from './admission/types';
import { dispositionChoices, dispositionFor, type CodedCause } from './admission/stay-codes';
import { CauseOfDeathField, SpecialtySelect, useSpecialties } from './admission/StayCodeFields';

const DISCHARGE_STATUSES = ['discharged', 'lama', 'referred', 'expired', 'absconded'];

export function DischargePage() {
  const { admissionId = '' } = useParams<{ admissionId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), `/ipd/admissions/${admissionId}`);

  const admission = useQuery({
    queryKey: ['admission', admissionId],
    queryFn: () => get<Admission>(`/ipd/admissions/${admissionId}`),
    enabled: Boolean(admissionId),
  });

  if (!admission.data) {
    return (
      <FormScreen title={tr('ipd', 'discharge')} icon={LogOut} backTo={returnTo}>
        <RecordState error={admission.error} missing={!admission.isLoading} label={tr('ipd', 'loading_the_admission')} />
      </FormScreen>
    );
  }

  return <DischargeForm key={admission.data.id} admission={admission.data} returnTo={returnTo} />;
}

function DischargeForm({ admission, returnTo }: { admission: Admission; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, hasModule } = useAuth();
  const nphies = hasModule('nphies');
  const [initialSummary] = useState(() => ({
    finalDiagnosis: admission.provisionalDiagnosis ?? '',
    procedures: '',
    hospitalCourse: '',
    conditionAtDischarge: '',
    medicationsOnDischarge: '',
    followUpInstructions: '',
  }));
  const [status, setStatus] = useState('discharged');
  const [summary, setSummary] = useState(initialSummary);
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [dispositionChoice, setDispositionChoice] = useState<string | null>(null);
  const [specialtyChoice, setSpecialtyChoice] = useState<string | null>(null);
  const [cause, setCause] = useState<CodedCause | null>(null);
  const [error, setError] = useState<string | null>(null);
  const specialties = useSpecialties(nphies);
  const expired = status === 'expired';
  const disposition = dispositionFor(status, dispositionChoice);
  const dischargeSpecialtyId = specialtyChoice ?? admission.doctor.specialtyId ?? '';

  const owing = admission.account.balanceMinor < 0;
  const mayOverrideBilling = can('discount.approve', 'discharge.override');

  const discharge = useMutation({
    mutationFn: () =>
      post<{ advanceRefundHeldBecause?: string | null; advanceHeldMinor?: number; chargesAfterDischarge?: { heldMinor: number } }>(`/ipd/admissions/${admission.id}/discharge`, {
        status,
        summary,
        overrideBillingClearance: override,
        overrideReason: override ? overrideReason : undefined,
        ...(nphies && disposition ? { dischargeDisposition: disposition } : {}),
        ...(nphies && dischargeSpecialtyId ? { dischargeSpecialtyId } : {}),
        ...(expired && cause ? { causeOfDeathCode: cause.code } : {}),
      }),
    onSuccess: (result) => {
      toast.success(tr('ipd', 'patient_discharged'), tr('ipd', 'gate_pass_issued_and_the_bed_sent_for_cleaning'));
      if (result?.advanceRefundHeldBecause) {
        toast.warning(tr('ipd', 'unused_advance_not_refunded'), tr('ipd', 'x_stays_on_account_x', { value: money(result.advanceHeldMinor ?? 0), advanceRefundHeldBecause: result.advanceRefundHeldBecause }));
      }
      if ((result?.chargesAfterDischarge?.heldMinor ?? 0) > 0) {
        toast.warning(
          tr('ipd', 'charges_after_the_discharge_day'),
          tr('ipd', 'x_was_billed_after_the_discharge_on_a_settled_bill', { value: money(result?.chargesAfterDischarge?.heldMinor ?? 0) }),
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['admission', admission.id] });
      void queryClient.invalidateQueries({ queryKey: ['admissions'] });
      void queryClient.invalidateQueries({ queryKey: ['bed-board'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const blocked = owing && override && overrideReason.trim().length < 5;
  const dirty =
    !discharge.isSuccess &&
    (status !== 'discharged' ||
      override ||
      overrideReason !== '' ||
      dispositionChoice !== null ||
      specialtyChoice !== null ||
      cause !== null ||
      JSON.stringify(summary) !== JSON.stringify(initialSummary));
  const openWork = admission.openWork;

  return (
    <FormScreen
      title={tr('ipd', 'discharge')}
      description={tr('ipd', 'the_summary_is_signed_by_you_and_printed_for')}
      eyebrow={`${admission.patient.fullName} · ${admission.number}`}
      icon={LogOut}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (blocked) return;
        setError(null);
        discharge.mutate();
      }}
    >
      {admission.bedChargeConfigured === false && (
        <Alert variant="warning" title={tr('ipd', 'this_bed_has_no_bed_day_charge')}>
          {tr('ipd', 'nothing_has_been_posted_for_the_stay_itself_set')}
        </Alert>
      )}
      {openWork && (openWork.labItems > 0 || openWork.radiology > 0 || openWork.dueDoses > 0) && (
        <Alert variant="warning" title={tr('ipd', 'work_still_open_on_this_admission')}>
          {[
            openWork.labItems > 0 ? `${openWork.labItems} lab test(s) not yet released` : null,
            openWork.radiology > 0 ? `${openWork.radiology} imaging study(ies) not yet released` : null,
            openWork.dueDoses > 0 ? `${openWork.dueDoses} medication dose(s) still due — active orders stop on discharge` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
          {tr('ipd', 'pending_results_will_still_land_on_the_patients')}
        </Alert>
      )}
      {owing && (
        <Alert variant="destructive" title={tr('ipd', 'account_short_by_x', { value: money(Math.abs(admission.account.balanceMinor)) })}>
          {mayOverrideBilling ? (
            <>
              <Checkbox
                checked={override}
                onCheckedChange={setOverride}
                label={tr('ipd', 'discharge_anyway_i_take_responsibility_for_the')}
                className="mt-1"
              />
              {override && (
                <Input
                  className="mt-2"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder={tr('ipd', 'reason_for_the_override')}
                />
              )}
            </>
          ) : (
            <p className="mt-1 text-sm">{tr('ipd', 'settle_the_account_to_discharge_only_a_head')}</p>
          )}
        </Alert>
      )}

      <FormSection title={tr('ipd', 'discharge_type')} icon={LogOut}>
        <Field label={tr('ipd', 'discharge_type')} required>
          <ChoiceButtons
            layout="chips"
            value={status}
            onChange={(next) => {
              setStatus(next);
              setDispositionChoice(null);
            }}
            required
            options={DISCHARGE_STATUSES.map((s) => ({ value: s, label: humanize(s) }))}
          />
        </Field>
        {nphies && (
          <Field label={tr('ipd', 'discharge_disposition')} hint={tr('ipd', 'discharge_disposition_hint')} className="sm:col-span-2">
            <ChoiceButtons layout="chips" size="sm" label={tr('ipd', 'discharge_disposition')} value={disposition} onChange={setDispositionChoice} options={dispositionChoices(status)} />
          </Field>
        )}
        {nphies && (
          <Field label={tr('ipd', 'discharge_specialty')} hint={tr('ipd', 'discharge_specialty_hint')}>
            <SpecialtySelect value={dischargeSpecialtyId} onChange={setSpecialtyChoice} specialties={specialties.data ?? []} />
          </Field>
        )}
        {expired && (
          <Field label={tr('ipd', 'cause_of_death_code')} hint={tr('ipd', 'cause_of_death_code_hint')} className="sm:col-span-2">
            <CauseOfDeathField value={cause} onChange={setCause} departmentId={admission.departmentId ?? null} />
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('ipd', 'discharge_summary')} icon={FileText}>
        <Field label={tr('ipd', 'final_diagnosis')} className="sm:col-span-2">
          <Input value={summary.finalDiagnosis} onChange={(e) => setSummary({ ...summary, finalDiagnosis: e.target.value })} />
        </Field>
        <Field label={tr('ipd', 'procedures')} className="sm:col-span-2">
          <Textarea rows={2} value={summary.procedures} onChange={(e) => setSummary({ ...summary, procedures: e.target.value })} />
        </Field>
        <Field label={tr('ipd', 'hospital_course')} className="sm:col-span-2">
          <Textarea rows={4} value={summary.hospitalCourse} onChange={(e) => setSummary({ ...summary, hospitalCourse: e.target.value })} />
        </Field>
        <Field label={tr('ipd', 'condition_at_discharge')}>
          <Textarea rows={2} value={summary.conditionAtDischarge} onChange={(e) => setSummary({ ...summary, conditionAtDischarge: e.target.value })} />
        </Field>
        <Field label={tr('ipd', 'medication_on_discharge')}>
          <Textarea rows={2} value={summary.medicationsOnDischarge} onChange={(e) => setSummary({ ...summary, medicationsOnDischarge: e.target.value })} />
        </Field>
        <Field label={tr('ipd', 'follow_up_instructions')} className="sm:col-span-2">
          <Textarea rows={2} value={summary.followUpInstructions} onChange={(e) => setSummary({ ...summary, followUpInstructions: e.target.value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('ipd', 'discharge')} loading={discharge.isPending} incomplete={blocked} cancelTo={returnTo} />
    </FormScreen>
  );
}
